// The one typed error every handler and hook on the platform throws when it
// wants a specific machine-readable code, instead of hand-building an envelope
// inline. `registerErrorHandler` (`@sportsmet/api-core`) is the only place that
// catches it and turns it into the wire envelope.
import { buildErrorEnvelope, type typeApiEnvelope } from "./envelope";
import {
  type ErrorCode,
  resolveClassCopy,
  resolveHttpStatus,
} from "./errorCodes";

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly details?: unknown;
  // A greppable throw-site marker, e.g. "#20260805-pick07" — also present as a
  // comment directly above the `throw` it names (rules.md "Error Handling").
  readonly ref?: string;

  constructor({
    code,
    details,
    ref,
  }: {
    code: ErrorCode;
    details?: unknown;
    ref?: string;
  }) {
    // The Error's own `.message` is always the canned class copy, never a raw
    // internal string — this class is not how you leak diagnostic detail. Put
    // whatever a developer needs to debug into `details` or the `ref`, not here.
    super(resolveClassCopy(code));
    this.name = "ApiError";
    this.code = code;
    this.details = details;
    // `exactOptionalPropertyTypes` makes `this.ref = ref` (where `ref` is
    // `string | undefined`) a type error against `readonly ref?: string` —
    // an optional field's assignable type is exactly `string`, not
    // `string | undefined`, under that flag. Assign only when present.
    if (ref !== undefined) {
      this.ref = ref;
    }
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

// Normalizes ANY thrown value into the wire envelope + the HTTP status to send
// it with. An `ApiError` keeps its own code; anything else (a genuinely
// unexpected exception) becomes `INTERNAL_ERROR` — the message sent to the
// client is always the class's canned copy, never the raw `error.message` or a
// stack trace. Pass `ref` so the client-visible `details.ref` can be grepped
// straight back to the catch site that produced it.
export function toErrorEnvelope(
  error: unknown,
  { ref }: { ref?: string } = {},
): { envelope: typeApiEnvelope<never>; httpStatus: number } {
  if (isApiError(error)) {
    return {
      envelope: buildErrorEnvelope({
        code: error.code,
        message: resolveClassCopy(error.code),
        details: error.details,
      }),
      httpStatus: resolveHttpStatus(error.code),
    };
  }

  return {
    envelope: buildErrorEnvelope({
      code: "INTERNAL_ERROR",
      message: resolveClassCopy("INTERNAL_ERROR"),
      details: ref ? { ref } : undefined,
    }),
    httpStatus: resolveHttpStatus("INTERNAL_ERROR"),
  };
}
